import { useState, useEffect } from 'react';
import { apiFetch } from './api.js';
import { useToast } from './ToastContext.jsx';

function money(field) {
  if (field == null) return null;
  if (typeof field === 'object') return field.amount ?? field;
  return field;
}

function adaptOrder(o) {
  return {
    id:       o.id,
    no:       o.no       ?? o.doc_no    ?? o.document_no,
    date:     o.date     ?? o.date_ordered,
    customer: o.customer ?? o.bp_name,
    status:   o.status   ?? o.doc_status,
    total:    money(o.total ?? o.grand_total),
    currency: o.currency ?? o.currency_iso_code ?? 'EUR',
  };
}

function adaptInvoice(i) {
  return {
    id:       i.id,
    no:       i.no   ?? i.doc_no ?? i.document_no,
    date:     i.date ?? i.date_invoiced,
    desc:     i.desc ?? i.description ?? '',
    status:   i.status ?? (i.is_paid ? 'Paid' : 'Open'),
    total:    money(i.total ?? i.grand_total),
    currency: i.currency ?? 'EUR',
    kind:     i.kind ?? i.document_type ?? 'AP',
  };
}

function adaptPayment(p) {
  return {
    ref:    p.ref    ?? p.doc_no ?? p.document_no,
    date:   p.date   ?? p.date_trx,
    method: p.method ?? p.tender_type,
    amount: money(p.amount ?? p.pay_amt),
    status: p.status ?? (p.is_reconciled ? 'Cleared' : 'Pending'),
  };
}

// Shared fetch-with-abort-and-toast-fallback behind useOrders/useInvoices/usePayments —
// same list-endpoint shape, only the endpoint/adapter/error message differ per resource.
function useFetchList(endpoint, adapt, errorMessage, enabled) {
  const { addToast } = useToast();
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    setLoading(true);
    apiFetch(endpoint, { signal: controller.signal })
      .then(raw => {
        const list = Array.isArray(raw) ? raw : (raw.data ?? []);
        setData(list.map(adapt));
      })
      .catch(err => { if (err.name !== 'AbortError' && err.status !== 403) addToast(errorMessage); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [enabled, addToast, endpoint, adapt, errorMessage]);

  return { data, loading };
}

export function useOrders(enabled = true) {
  const { data: orders, loading } = useFetchList('/orders?limit=50', adaptOrder, 'Could not load orders.', enabled);
  return { orders, loading };
}

export function useInvoices(enabled = true) {
  const { data: invoices, loading } = useFetchList('/invoices?limit=50', adaptInvoice, 'Could not load invoices.', enabled);
  return { invoices, loading };
}

export function usePayments(enabled = true) {
  const { data: payments, loading } = useFetchList('/payments?limit=50', adaptPayment, 'Could not load payments.', enabled);
  return { payments, loading };
}
